import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { lottieAnimations } from '@backrs_ui/common-ui/src/assets/lottieAnimations';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { BaseFileUpload } from '@backrs_ui/common-ui/src/molecules/FileUpload/BaseFileUpload';

import { SxProps, Theme, Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import Lottie from 'lottie-react';

const { fileUpload } = appStrings.modules.profile.editProfile;

export type FileUploadProps = Readonly<{
  onFileSelect: (files: File | File[]) => void;
  onError?: (error: Error) => void;
  acceptedFileTypes: string[];
  maxFileSize?: number; // in bytes
  inputId?: string;
  additionalText?: string | null;
  dragAndDropText?: string;
  multiple?: boolean;
  borderVariant?: 'solid' | 'dashed';
  children?: React.ReactNode;
}>;

export const DocumentFileUpload = ({
  onFileSelect,
  onError,
  acceptedFileTypes,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  inputId = 'file-upload',
  additionalText = '',
  dragAndDropText = fileUpload.dragAndDrop,
  multiple = false,
  borderVariant = 'solid',
  children,
}: FileUploadProps) => {
  const handleFileSelect = async (files: File | File[]) => {
    try {
      await onFileSelect(files);
    } catch (error) {
      console.error('Error processing files:', error);
      onError?.(error as Error);
    }
  };

  return (
    <BaseFileUpload
      onFileSelect={handleFileSelect}
      onError={onError}
      acceptedFileTypes={acceptedFileTypes}
      maxFileSize={maxFileSize}
      className="document-file-upload"
      containerSx={dragAndDropSx(borderVariant)}
      inputId={inputId}
      multiple={multiple}
    >
      {({ isLoading }) =>
        !children ? (
          <Box sx={dragAndDropStyle}>
            <Box sx={uploadViewContainerSx}>
              {isLoading ? (
                <Box sx={loadingContainerSx}>
                  <Lottie animationData={lottieAnimations.dotsDotting} loop />
                </Box>
              ) : (
                <Box sx={iconContainerSx}>
                  <AppIcon iconName="UploadCloud02" diameter="20px" color={'Gray/700'} />
                </Box>
              )}
              <Typography>
                <Typography
                  component="span"
                  variant="text-sm-semibold"
                  color={(theme: Theme) => theme.backrs.colors['Brand/700']}
                >
                  {fileUpload.clickToUpload}
                </Typography>{' '}
                <Typography
                  component="span"
                  variant="text-sm-regular"
                  color={(theme: Theme) => theme.backrs.colors['Gray/600']}
                >
                  {dragAndDropText}
                </Typography>
                <Typography
                  variant="text-xs-regular"
                  color={(theme: Theme) => theme.backrs.colors['Gray/600']}
                >
                  {fileUpload.acceptedFileTypes(
                    acceptedFileTypes,
                    additionalText || fileUpload.maxFileSize(maxFileSize),
                  )}
                </Typography>
              </Typography>
            </Box>
          </Box>
        ) : (
          children
        )
      }
    </BaseFileUpload>
  );
};

const commonIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: backrs.spacing['spacing-5xl'],
  height: backrs.spacing['spacing-5xl'],
  background: backrs.colors['Colors/Base/White'],
  boxShadow: backrs.shadows['shadow-xs-skeuomorphic'],
  border: backrs.colors['Gray/200'],
  borderRadius: backrs.radius['radius-md'],
});

const loadingContainerSx: SxProps<Theme> = (theme: Theme) => ({
  flexDirection: 'column',
  ...commonIconSx(theme),
});

const iconContainerSx: SxProps<Theme> = (theme: Theme) => ({
  padding: `calc(${theme.backrs.spacing['spacing-lg']} - ${theme.backrs.spacing['spacing-xxs']})`,
  ...commonIconSx(theme),
});

const uploadViewContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignContent: 'center',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  pointerEvents: 'none',
  textAlign: 'center',
});

const dragAndDropSx =
  (borderVariant: 'solid' | 'dashed'): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    '&.dragover': {
      backgroundColor: backrs.colors['Gray/50'],
      border: `1px solid ${backrs.colors['Brand/500']}`,
    },
    borderRadius: backrs.radius['radius-xl'],
    transition: 'all 0.3s ease-in-out',
    border:
      borderVariant === 'dashed'
        ? `1px dashed ${backrs.colors['Gray/300']}`
        : `1px solid ${backrs.colors['Gray/200']}`,
    width: '100%',
  });

const dragAndDropStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  padding: `${backrs.spacing['spacing-xl']} ${backrs.spacing['spacing-3xl']}`,
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  borderRadius: backrs.radius['radius-xl'],
  background: backrs.colors['Colors/Base/White'],
  position: 'relative',
  width: '100%',
  animation: `${keyframes`
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `} 0.3s ease-out`,
});
